import { captureScreenshot } from "./screenshot.js";
import { annotateImage } from "./annotate.js";

// REUSABLE builder→app screenshot bridge. The platform editor (parent frame)
// posts { type: "CAPTURE_SCREENSHOT" }; we capture the app document, open the
// annotate overlay for the user to draw, and post the result back:
//   { type: "SCREENSHOT_CAPTURED", dataUrl }  on success
//   { type: "SCREENSHOT_CANCELLED" }          on cancel / capture failure
// Always installed (NOT gated on the Team Notes setting).
let inFlight = false;

// SECURITY: the served app has no frame-ancestors/X-Frame-Options, so any site
// could embed a logged-in user's app and request a screenshot of their
// authenticated DOM (PII/tokens). Only honor CAPTURE_SCREENSHOT from the
// AgentUI builder origin (or the app's own custom domain), and reply to that
// exact origin — never "*".
function isTrustedParent(origin) {
    try {
        const { hostname } = new URL(origin);
        if (
            hostname === "localhost" ||
            hostname === "agentui.ai" ||
            hostname.endsWith(".agentui.ai") ||
            hostname.endsWith(".agentui.test")
        ) {
            return true;
        }
        const custom = window.config?.customDomain;
        if (custom && (hostname === custom || hostname.endsWith(`.${custom}`))) {
            return true;
        }
        return false;
    } catch {
        return false;
    }
}

export function installCaptureBridge() {
    if (typeof window === "undefined" || window.parent === window) return;
    window.addEventListener("message", async (event) => {
        if (event.data?.type !== "CAPTURE_SCREENSHOT") return;
        if (!isTrustedParent(event.origin)) return;
        if (inFlight) return;
        inFlight = true;
        const reply = (msg) => event.source?.postMessage(msg, event.origin);
        try {
            const captured = await captureScreenshot();
            if (!captured) {
                reply({ type: "SCREENSHOT_CANCELLED", reason: "capture-failed" });
                return;
            }
            const annotated = await annotateImage(captured);
            reply(
                annotated
                    ? { type: "SCREENSHOT_CAPTURED", dataUrl: annotated }
                    : { type: "SCREENSHOT_CANCELLED", reason: "user-cancelled" }
            );
        } finally {
            inFlight = false;
        }
    });
}
